import {UploadStrategy, UploadStrategyConfig} from './strategy/upload-strategy';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import {Disk} from './backend-metadata';
import {S3MultipartUpload} from './strategy/s3-multipart-upload';
import {S3Upload} from './strategy/s3-upload';
import {TusUpload} from './strategy/tus-upload';
import {AxiosUpload} from './strategy/axios-upload';
import {UserApiUpload} from './strategy/user-api-upload';
import {FileUpload, FileUploadState} from './file-upload-store';
import {validateFile} from '@ui/utils/files/validate-file';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {toast} from '@ui/toast/toast';
import {ProgressTimeout} from './progress-timeout';
import {message} from '@ui/i18n/message';

function getTenantFolderName(): string | null {
  const bootstrapData = getBootstrapData();
  const tenant = bootstrapData?.current_tenant;

  if (tenant?.domain) {
    return tenant.domain;
  }

  if (tenant?.subdomain) {
    return tenant.subdomain;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const host = window.location.host;
  let main = '';

  if (typeof document !== 'undefined') {
    main =
      document
        .querySelector('meta[name="app-domain"]')
        ?.getAttribute('content') || '';
  }

  if (!main) {
    const baseUrl = bootstrapData?.settings?.base_url;
    if (baseUrl) {
      try {
        main = new URL(baseUrl).host;
      } catch (e) {
        main = '';
      }
    }
  }

  if (main && host === main) {
    return null;
  }

  if (main && host.endsWith(`.${main}`)) {
    const subdomain = host.slice(0, -1 * (main.length + 1));
    return subdomain || null;
  }

  return host || null;
}

export async function startUploading(
  upload: FileUpload,
  state: FileUploadState,
): Promise<UploadStrategy | null> {
 
  const settings = getBootstrapData().settings;
  const options = upload.options;
  const file = upload.file as UploadedFile;

  // validate file, if validation fails, error the upload and bail
  if (options?.restrictions) {
    const errorMessage = validateFile(file, options.restrictions);
    if (errorMessage) {
      console.log('File validation failed:', errorMessage);
      state.updateFileUpload(file.id, {
        errorMessage,
        status: 'failed',
        request: undefined,
        timer: undefined,
      });

      if (options.showToastOnRestrictionFail) {
        toast.danger(errorMessage);
      }

      state.runQueue();
      return null;
    }
    console.log('File validation passed');
  }

  // prepare config for file upload strategy
  const timer = new ProgressTimeout();
  const config: UploadStrategyConfig = {
    metadata: {
      ...options?.metadata,
      relativePath: file.relativePath,
      disk: options?.metadata?.disk || Disk.uploads,
      parentId: options?.metadata?.parentId ?? null,
    },
    chunkSize: settings.uploads.chunk_size,
    baseUrl: settings.base_url,
    onError: errorMessage => {
      state.updateFileUpload(file.id, {
        errorMessage,
        status: 'failed',
      });
      state.runQueue();
      timer.done();
      options?.onError?.(errorMessage, file);
      if (options.showToastOnBackendError && errorMessage) {
        toast.danger(errorMessage);
      }
    },
    onSuccess: entry => {
      state.updateFileUpload(file.id, {
        status: 'completed',
        entry,
      });
      state.runQueue();
      timer.done();
      options?.onSuccess?.(entry, file);
    },
    onProgress: ({bytesUploaded, bytesTotal}) => {
      const percentage = (bytesUploaded / bytesTotal) * 100;
      state.updateFileUpload(file.id, {
        percentage,
        bytesUploaded,
      });
      timer.progress();
      options?.onProgress?.({bytesUploaded, bytesTotal});
    },
  };

  const disk = config.metadata?.disk || Disk.uploads;
  const driver =
    disk === Disk.uploads
      ? settings.uploads.uploads_driver
      : settings.uploads.public_driver;

  if (driver === 'user_api') {
    // Backend automatically handles tenant/user folder structure
    // No need to set relativePath or tenant folder name
    config.metadata = {
      ...config.metadata,
      userApiParentId: null,
    };
  }

  // choose and create upload strategy, based on file size and settings
  console.log('Choosing upload strategy...');
  const strategy = chooseUploadStrategy(file, config);
  console.log('Strategy chosen:', strategy.name);
  
  try {
    console.log('Creating upload request...');
    const request = await strategy.create(file, config);
    console.log('Upload request created successfully');

    // add handler for when upload times out (no progress for 30+ seconds)
    timer.timeoutHandler = () => {
      console.log('Upload timed out');
      request.abort();
      state.updateFileUpload(file.id, {
        status: 'failed',
        errorMessage: message('Upload timed out'),
      });
      state.runQueue();
    };

    console.log('Starting upload...');
    state.updateFileUpload(file.id, {
      status: 'inProgress',
      request,
    });
    request.start();
    console.log('Upload started successfully');

    return request;
  } catch (error) {
    console.error('Error during upload:', error);
    state.updateFileUpload(file.id, {
      status: 'failed',
      errorMessage: error instanceof Error ? error.message : 'Upload failed',
    });
    state.runQueue();
    return null;
  }
}

const OneMB = 1024 * 1024;
const FourMB = 4 * OneMB;
const HundredMB = 100 * OneMB;

const chooseUploadStrategy = (
  file: UploadedFile,
  config: UploadStrategyConfig,
) => {
  const settings = getBootstrapData().settings;
  const disk = config.metadata?.disk || Disk.uploads;
  const driver =
    disk === Disk.uploads
      ? settings.uploads.uploads_driver
      : settings.uploads.public_driver;

  console.log('chooseUploadStrategy:', {
    disk,
    driver,
    uploads_driver: settings.uploads.uploads_driver,
    user_api_url: (settings.uploads as any)?.user_api_url,
    user_api_token: (settings.uploads as any)?.user_api_token ? 'SET' : 'NOT SET',
  });

  if (driver === 'user_api') {
    console.log('Selected: UserApiUpload');
    return UserApiUpload;
  }
  
  if (driver?.endsWith('s3') && settings.uploads.s3_direct_upload) {
    console.log('Selected: S3 Upload');
    return file.size >= HundredMB ? S3MultipartUpload : S3Upload;
  } else {
    console.log('Selected: TUS/Axios Upload');
    return file.size >= FourMB && !settings.uploads.disable_tus
      ? TusUpload
      : AxiosUpload;
  }
};
